/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/HRPortal/BHXHCompliance.tsx
 * PURPOSE: HR BHXH Compliance Portal - EPIC 10.2 Automated BHXH calculation.
 *          An API failure shows an error and PENDING_REAL_DATA shows an empty
 *          state; the page never renders invented staff counts, rates or
 *          amounts (audit A2, 2026-10-05).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Automated BHXH calculation with monthly submission report generation
 *
 * @ai-human-orchestration
 * - AIRole: Senior Frontend Developer + Compliance Engineer
 * - HumanHandoff: Local Team validates Vietnamese compliance rules
 * - CollaborationLevel: Remote Team implements, HR Officer validates
 *
 * @business-context
 * - Domain: Vietnamese F&B BHXH Compliance
 * - Impact: Compliance Critical - ₫35.1.3B+ penalty risk exposure
 * - Stakeholders: NQH HR Officer, CFO, Vietnamese Government (BHXH)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Dashboard Page
 * - Pattern: React Component, CSS Modules, NQH Brand Design System
 * - Dependencies: React 18, TypeScript, CSS Modules
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - API: docs/03-integrate/01-API-Specifications/
 *
 * Sprint 10 EPIC 10.2: BHXH Compliance Automation (6 SP)
 * Features:
 * - BHXH calculator (per employee)
 * - Monthly submission report generation (one-click export)
 * - Compliance checklist dashboard (8 policies)
 * - Exception handling interface (violation review + resolution)
 * - ₫35.1.3B+ penalty risk dashboard
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/services/api/config';
import { useAuthStore } from '../../store/auth';
import { getTenantDisplayText } from '../../config/tenant';
import {
  bhxhComplianceService,
  BHXH_PENDING_REAL_DATA,
  BHXH_RATES_2025,
  BHXHSubmissionReport,
  PenaltyRiskResponse,
  LaborLawValidationResponse,
  formatVND,
  getComplianceStatusBadge,
  getRiskLevelBadge,
  getPolicyStatusDisplay,
  formatVietnameseMonth,
} from '../../services/api/bhxhComplianceService';
import styles from './BHXHCompliance.module.css';

// ============= Component State Types =============

interface PortalState {
  activeTab: 'submission' | 'compliance' | 'risk';
  selectedMonth: string;
}

// ============= HR BHXH Compliance Portal Component =============

export const BHXHCompliance: React.FC = () => {
  const { user, logout } = useAuthStore();

  // Portal state
  const [state, setState] = useState<PortalState>({
    activeTab: 'submission',
    selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM
  });

  // Data state
  const [submissionReport, setSubmissionReport] = useState<BHXHSubmissionReport | null>(null);
  const [penaltyRisk, setPenaltyRisk] = useState<PenaltyRiskResponse | null>(null);
  const [laborLawValidation, setLaborLawValidation] = useState<LaborLawValidationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data on mount and state changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [reportData, riskData, validationData] = await Promise.all([
          bhxhComplianceService.getSubmissionReport(state.selectedMonth),
          bhxhComplianceService.getPenaltyRisk(),
          bhxhComplianceService.validateLaborLaw(),
        ]);

        setSubmissionReport(reportData);
        setPenaltyRisk(riskData);
        setLaborLawValidation(validationData);
      } catch (err) {
        console.warn('BHXH API unavailable:', err instanceof Error ? err.message : err);
        setError('Không tải được dữ liệu BHXH từ máy chủ. Vui lòng thử lại sau.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [state.selectedMonth]);

  // Export to Excel handler - SPRINT 10 EPIC 10.2
  const handleExportSubmission = async () => {
    try {
      // Build export URL with current month filter
      const params = new URLSearchParams();
      if (state.selectedMonth) {
        params.append('submission_month', state.selectedMonth);
      }

      // Direct download via browser
      const exportUrl = `${API_BASE_URL}/api/v1/bhxh/submission-report/export?${params.toString()}`;

      // Create hidden link and trigger download
      const link = document.createElement('a');
      link.href = exportUrl;
      link.setAttribute('download', `bhxh_report_${state.selectedMonth || new Date().toISOString().slice(0,7)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Lỗi khi xuất báo cáo BHXH. Vui lòng thử lại.');
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Đang tải dữ liệu BHXH...</p>
        </div>
      </div>
    );
  }

  // Error state: say what failed; never show placeholder figures (audit A2)
  if (error) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <h1 className={styles.title}>🏥 HR Portal - BHXH Compliance</h1>
            </div>
            <div className={styles.headerRight}>
              <button className={styles.logoutButton} onClick={() => logout()}>Đăng xuất</button>
            </div>
          </div>
        </header>
        <main className={styles.main}>
          <section className={styles.tabContent} role="alert">
            <h2 className={styles.sectionTitle}>Lỗi tải dữ liệu</h2>
            <p>{error}</p>
          </section>
        </main>
      </div>
    );
  }

  // DD-0067: while the backend has no real salary data it answers 200 with
  // data_status PENDING_REAL_DATA, zero totals, "pending" policies and a
  // "compliant" risk verdict derived from no checks. Rendering those would read
  // as "0 staff", "Không đạt" x8 and "Doanh nghiệp đang tuân thủ", so show the
  // backend's own message instead, on every tab.
  const pendingMessage = [submissionReport, laborLawValidation].some(
    (d) => d?.data_status === BHXH_PENDING_REAL_DATA,
  )
    ? submissionReport?.data_status_message_vi || laborLawValidation?.data_status_message_vi || ''
    : null;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.titleBadge}>
              <h1 className={styles.title}>🏥 HR Portal - BHXH Compliance</h1>
            </div>
            <div className={styles.roleBadge}>HR Officer View</div>
          </div>

          <div className={styles.headerRight}>
            <div className={styles.dateInfo}>
              <div className={styles.dateLabel}>
                {new Date().toLocaleDateString('vi-VN', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className={styles.userName}>{user?.full_name || 'HR Officer'}</div>
            </div>
            <button className={styles.logoutButton} onClick={() => logout()}>
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* BHXH Rates Info Banner */}
        <section className={styles.ratesBanner}>
          <h3 className={styles.ratesBannerTitle}>📋 Quy Định BHXH 2025</h3>
          <div className={styles.ratesGrid}>
            <div className={styles.rateItem}>
              <span className={styles.rateLabel}>Doanh nghiệp đóng:</span>
              <span className={styles.rateValue}>{(BHXH_RATES_2025.employer * 100).toFixed(1)}%</span>
            </div>
            <div className={styles.rateItem}>
              <span className={styles.rateLabel}>Người lao động đóng:</span>
              <span className={styles.rateValue}>{(BHXH_RATES_2025.employee * 100).toFixed(1)}%</span>
            </div>
            <div className={styles.rateItem}>
              <span className={styles.rateLabel}>Tổng cộng:</span>
              <span className={styles.rateValue}>{(BHXH_RATES_2025.total * 100).toFixed(1)}%</span>
            </div>
            <div className={styles.rateItem}>
              <span className={styles.rateLabel}>Mức trần:</span>
              <span className={styles.rateValue}>{formatVND(BHXH_RATES_2025.ceiling)}</span>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className={styles.tabsContainer}>
          {[
            { id: 'submission', label: '📝 Báo Cáo Đóng BHXH', icon: '📝' },
            { id: 'compliance', label: '✅ Tuân Thủ Luật LĐ', icon: '✅' },
            { id: 'risk', label: '⚠️ Rủi Ro Xử Phạt', icon: '⚠️' },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tabButton} ${state.activeTab === tab.id ? styles.tabButtonActive : ''}`}
              onClick={() => setState({ ...state, activeTab: tab.id as PortalState['activeTab'] })}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {pendingMessage !== null && (
          <section className={styles.tabContent} role="status">
            <h3 className={styles.sectionTitle}>Chưa có dữ liệu</h3>
            <p>{pendingMessage}</p>
          </section>
        )}

        {/* Submission Report Tab */}
        {pendingMessage === null && state.activeTab === 'submission' && submissionReport && (
          <section className={styles.tabContent}>
            {/* Month Selector */}
            <div className={styles.monthSelector}>
              <label className={styles.monthLabel}>Tháng báo cáo:</label>
              <input
                type="month"
                className={styles.monthInput}
                value={state.selectedMonth}
                onChange={(e) => setState({ ...state, selectedMonth: e.target.value })}
              />
              <button className={styles.exportButton} onClick={handleExportSubmission}>
                📊 Xuất Báo Cáo Excel
              </button>
            </div>

            {/* Summary Cards */}
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}>👥</div>
                <div className={styles.summaryContent}>
                  <div className={styles.summaryLabel}>Tổng Nhân Viên</div>
                  <div className={styles.summaryValue}>{submissionReport.summary.total_employees}</div>
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}>🏢</div>
                <div className={styles.summaryContent}>
                  <div className={styles.summaryLabel}>DN Đóng</div>
                  <div className={styles.summaryValue}>
                    {formatVND(submissionReport.summary.total_employer_contribution)}
                  </div>
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}>👤</div>
                <div className={styles.summaryContent}>
                  <div className={styles.summaryLabel}>NLĐ Đóng</div>
                  <div className={styles.summaryValue}>
                    {formatVND(submissionReport.summary.total_employee_contribution)}
                  </div>
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}>💰</div>
                <div className={styles.summaryContent}>
                  <div className={styles.summaryLabel}>Tổng Đóng</div>
                  <div className={styles.summaryValue}>
                    {formatVND(submissionReport.summary.total_combined_contribution)}
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Contributions Table */}
            <div className={styles.tableSection}>
              <h3 className={styles.tableSectionTitle}>
                📋 Chi Tiết Đóng BHXH - {formatVietnameseMonth(submissionReport.submission_month)}
              </h3>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Họ Tên</th>
                      <th className={styles.th}>Số BHXH</th>
                      <th className={styles.th}>Lương Cơ Bản</th>
                      <th className={styles.th}>Mức Đóng</th>
                      <th className={styles.th}>DN Đóng</th>
                      <th className={styles.th}>NLĐ Đóng</th>
                      <th className={styles.th}>Tổng</th>
                      <th className={styles.th}>Trần</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissionReport.employees.slice(0, 20).map((emp, idx) => (
                      <tr key={idx} className={styles.tr}>
                        <td className={styles.td}>
                          <strong>{emp.employee_name}</strong>
                        </td>
                        <td className={styles.td}>{emp.bhxh_number}</td>
                        <td className={styles.td}>{formatVND(emp.base_salary)}</td>
                        <td className={styles.td}>{formatVND(emp.contribution_base)}</td>
                        <td className={styles.td}>{formatVND(emp.employer_contribution)}</td>
                        <td className={styles.td}>{formatVND(emp.employee_contribution)}</td>
                        <td className={styles.td}>
                          <strong>{formatVND(emp.total_contribution)}</strong>
                        </td>
                        <td className={styles.td}>
                          {emp.ceiling_applied ? (
                            <span className={styles.ceilingBadge}>Áp dụng trần</span>
                          ) : (
                            <span className={styles.noCeilingBadge}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {submissionReport.employees.length > 20 && (
                <div className={styles.tableFooter}>
                  Hiển thị 20/{submissionReport.employees.length} nhân viên. Xuất Excel để xem đầy đủ.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Labor Law Compliance Tab */}
        {pendingMessage === null && state.activeTab === 'compliance' && laborLawValidation && (
          <section className={styles.tabContent}>
            {/* Compliance Summary */}
            <div className={styles.complianceSummary}>
              <div className={styles.complianceHeader}>
                <div>
                  <h3 className={styles.complianceTitle}>
                    Kiểm Tra Tuân Thủ Luật Lao Động {laborLawValidation.vietnamese_labor_law_version}
                  </h3>
                  <p className={styles.complianceDate}>
                    Ngày kiểm tra: {new Date(laborLawValidation.compliance_date).toLocaleDateString('vi-VN')}
                  </p>
                </div>
                <div
                  className={styles.complianceStatusBadge}
                  style={{
                    background: getComplianceStatusBadge(laborLawValidation.overall_status).bgColor,
                    color: getComplianceStatusBadge(laborLawValidation.overall_status).color,
                  }}
                >
                  {getComplianceStatusBadge(laborLawValidation.overall_status).text}
                </div>
              </div>

              <div className={styles.complianceStats}>
                <div className={styles.complianceStat}>
                  <div className={styles.complianceStatValue}>{laborLawValidation.policies_checked}</div>
                  <div className={styles.complianceStatLabel}>Chính sách kiểm tra</div>
                </div>
                <div className={styles.complianceStat}>
                  <div className={`${styles.complianceStatValue} ${styles.complianceStatValuePassed}`}>
                    {laborLawValidation.policies_passed}
                  </div>
                  <div className={styles.complianceStatLabel}>Đạt yêu cầu</div>
                </div>
                <div className={styles.complianceStat}>
                  <div className={styles.complianceStatValue}>{laborLawValidation.compliance_rate}</div>
                  <div className={styles.complianceStatLabel}>Tỷ lệ tuân thủ</div>
                </div>
              </div>
            </div>

            {/* 8 Policies Checklist */}
            <div className={styles.policiesGrid}>
              {laborLawValidation.policies.map((policy, idx) => {
                const statusDisplay = getPolicyStatusDisplay(policy.status);
                return (
                  <div
                    key={idx}
                    className={styles.policyCard}
                    style={{ borderColor: statusDisplay.color }}
                  >
                    <div className={styles.policyHeader}>
                      <span
                        className={styles.policyIcon}
                        style={{ color: statusDisplay.color }}
                      >
                        {statusDisplay.icon}
                      </span>
                      <span className={styles.policyName}>{policy.name}</span>
                    </div>
                    <p className={styles.policyDescription}>{policy.description}</p>
                    <div className={styles.policyFooter}>
                      <span
                        className={styles.policyStatus}
                        style={{
                          background:
                            policy.status === 'pass'
                              ? 'rgba(34, 197, 94, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                          color: statusDisplay.color,
                        }}
                      >
                        {statusDisplay.text}
                      </span>
                      {policy.violations > 0 && (
                        <span className={styles.policyViolations}>{policy.violations} vi phạm</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Penalty Risk Tab */}
        {pendingMessage === null && state.activeTab === 'risk' && penaltyRisk && (
          <section className={styles.tabContent}>
            {/* Risk Overview */}
            <div className={styles.riskOverview}>
              <div className={styles.riskHeader}>
                <h3 className={styles.riskTitle}>⚠️ Đánh Giá Rủi Ro Xử Phạt</h3>
                <div
                  className={styles.riskLevelBadge}
                  style={{
                    background: getRiskLevelBadge(penaltyRisk.risk_level).bgColor,
                    color: getRiskLevelBadge(penaltyRisk.risk_level).color,
                  }}
                >
                  Mức độ: {getRiskLevelBadge(penaltyRisk.risk_level).text}
                </div>
              </div>

              {/* Risk Breakdown */}
              <div className={styles.riskBreakdown}>
                <div className={styles.riskCard}>
                  <div className={styles.riskCardIcon}>🏥</div>
                  <div className={styles.riskCardContent}>
                    <div className={styles.riskCardLabel}>Rủi ro BHXH</div>
                    <div className={styles.riskCardValue}>
                      {formatVND(penaltyRisk.assessment.risk_breakdown.bhxh_risk)}
                    </div>
                  </div>
                </div>
                <div className={styles.riskCard}>
                  <div className={styles.riskCardIcon}>⚖️</div>
                  <div className={styles.riskCardContent}>
                    <div className={styles.riskCardLabel}>Rủi ro Luật LĐ</div>
                    <div className={styles.riskCardValue}>
                      {formatVND(penaltyRisk.assessment.risk_breakdown.labor_law_risk)}
                    </div>
                  </div>
                </div>
                <div className={styles.riskCard}>
                  <div className={styles.riskCardIcon}>💵</div>
                  <div className={styles.riskCardContent}>
                    <div className={styles.riskCardLabel}>Rủi ro Thuế</div>
                    <div className={styles.riskCardValue}>
                      {formatVND(penaltyRisk.assessment.risk_breakdown.tax_risk)}
                    </div>
                  </div>
                </div>
                <div className={`${styles.riskCard} ${styles.riskCardTotal}`}>
                  <div className={styles.riskCardIcon}>💰</div>
                  <div className={styles.riskCardContent}>
                    <div className={styles.riskCardLabel}>TỔNG RỦI RO</div>
                    <div className={`${styles.riskCardValue} ${styles.riskCardValueTotal}`}>
                      {formatVND(penaltyRisk.assessment.risk_breakdown.total_risk)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Compliance Status */}
              <div
                className={`${styles.complianceStatusBanner} ${
                  penaltyRisk.compliance_status === 'compliant'
                    ? styles.complianceStatusBannerCompliant
                    : styles.complianceStatusBannerNonCompliant
                }`}
              >
                <span className={styles.statusIcon}>
                  {penaltyRisk.compliance_status === 'compliant' ? '✅' : '⚠️'}
                </span>
                <span className={styles.statusText}>
                  {penaltyRisk.compliance_status === 'compliant'
                    ? 'Doanh nghiệp đang tuân thủ các quy định. Tiếp tục duy trì!'
                    : 'Cần xem xét và khắc phục các vi phạm để tránh rủi ro xử phạt.'}
                </span>
              </div>

              {/* Recommended Actions */}
              <div className={styles.recommendedActions}>
                <h4 className={styles.actionsTitle}>📋 Hành Động Khuyến Nghị</h4>
                <div className={styles.actionsList}>
                  {penaltyRisk.recommended_actions.map((action, idx) => (
                    <div key={idx} className={styles.actionItem}>
                      <span
                        className={`${styles.actionPriority} ${
                          action.priority === 'critical'
                            ? styles.actionPriorityCritical
                            : action.priority === 'high'
                            ? styles.actionPriorityHigh
                            : action.priority === 'medium'
                            ? styles.actionPriorityMedium
                            : styles.actionPriorityLow
                        }`}
                      >
                        {action.priority.toUpperCase()}
                      </span>
                      <span className={styles.actionText}>{action.action}</span>
                      <span className={styles.actionDeadline}>Thời hạn: {action.deadline}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Violations List (if any) */}
              {penaltyRisk.assessment.violations_count > 0 && (
                <div className={styles.violationsSection}>
                  <h4 className={styles.violationsTitle}>
                    🚨 Vi Phạm Phát Hiện ({penaltyRisk.assessment.violations_count})
                  </h4>
                  <div className={styles.violationsList}>
                    {penaltyRisk.assessment.violations.map((violation, idx) => (
                      <div key={idx} className={styles.violationItem}>
                        <div className={styles.violationHeader}>
                          <span className={styles.violationType}>{violation.violation_type}</span>
                          <span
                            className={styles.violationSeverity}
                            style={{ color: getRiskLevelBadge(violation.severity).color }}
                          >
                            {getRiskLevelBadge(violation.severity).text}
                          </span>
                        </div>
                        <p className={styles.violationDescription}>{violation.description}</p>
                        <div className={styles.violationFooter}>
                          <span>Mức phạt tiềm năng: {formatVND(violation.potential_penalty)}</span>
                          {violation.employee_name && <span>NV: {violation.employee_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <span>Sprint 10 EPIC 10.2 - BHXH Compliance Portal</span>
          <span>{getTenantDisplayText()} | Luật Lao Động 2019 | BHXH 2025</span>
        </div>
      </footer>
    </div>
  );
};

export default BHXHCompliance;
